'use client';

import SafeImage from '@c/ui/SafeImage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@util/index';
import {
  deleteProductComment,
  getProductComments,
  postProductComment,
} from '@util/firestore/comments';
import { useAuth } from 'context/AuthContext';
import type { CommentDocument } from 'models/comment';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/solid';

const MAX_DEPTH = 2;
const INDENT_PX = 40;

function formatCommentDate(created: number): string {
  const now = Date.now();
  const diffMs = Math.max(0, now - created);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (diffMs < minute) return 'just now';
  if (diffMs < hour) {
    const mins = Math.floor(diffMs / minute);
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diffMs < day) {
    const hours = Math.floor(diffMs / hour);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffMs < week) {
    const days = Math.floor(diffMs / day);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  if (diffMs < 30 * day) {
    const weeks = Math.floor(diffMs / week);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  return new Date(created).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function avatarFallback(username: string) {
  const initials = (username || 'NA').slice(0, 2);
  return `https://ui-avatars.com/api/?background=random&length=2&name=${initials}&size=256`;
}

function CommentAvatar({ src, username }: { src?: string; username: string }) {
  return (
    <div className="relative size-[5rem] shrink-0 overflow-hidden rounded-full">
      <SafeImage
        src={src || avatarFallback(username)}
        fallbackSrc={avatarFallback(username)}
        alt={username || 'User'}
        width={100}
        height={100}
        fill
        style={{ objectFit: 'cover' }}
      />
    </div>
  );
}

interface InlineCommentFormProps {
  productId: string;
  replyingTo?: string | null;
  currentUserPhoto?: string;
  currentUsername?: string;
  autoFocus?: boolean;
  onCancel?: () => void;
  onPosted?: () => void;
  placeholder?: string;
}

function InlineCommentForm({
  productId,
  replyingTo = null,
  currentUserPhoto,
  currentUsername = '',
  autoFocus = false,
  onCancel,
  onPosted,
  placeholder = 'Leave a comment',
}: InlineCommentFormProps) {
  const [value, setValue] = useState('');
  const queryClient = useQueryClient();
  const { userDoc } = useAuth();

  const mutation = useMutation({
    mutationFn: () =>
      postProductComment(
        {
          product_id: productId,
          comment: value.trim(),
          replying_to: replyingTo,
        },
        {
          uid: userDoc?.uid ?? '',
          username: userDoc?.username,
          photo: userDoc?.photo,
        }
      ),
    onSuccess: (newComment) => {
      setValue('');
      queryClient.setQueryData<CommentDocument[]>(
        ['productComments', productId],
        (prev = []) => [...prev, newComment]
      );
      onPosted?.();
    },
  });

  const canSubmit = value.trim().length > 0 && !mutation.isPending;

  return (
    <div className="flex w-full flex-col items-end justify-center gap-[1.6rem] px-8 py-[1.6rem] sm:px-0">
      <div className="flex w-full items-start gap-[1rem]">
        <CommentAvatar src={currentUserPhoto} username={currentUsername} />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-[0.4rem]">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            disabled={mutation.isPending}
            className="h-[18rem] w-full resize-none rounded-[2rem] bg-zinc-100 px-[1.6rem] py-[1.2rem] text-[1.6rem] font-medium leading-[1.5] text-black placeholder:text-black/50 focus:outline-none disabled:opacity-60"
          />
          {mutation.isError && (
            <p className="text-[1.3rem] font-medium text-brand-secondary">
              Could not post your comment. Please try again.
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-[0.8rem]">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={mutation.isPending}
            className="flex h-[4.4rem] items-center justify-center rounded-full px-[2rem] text-[1.6rem] font-semibold leading-[1.5] text-black hover:underline disabled:opacity-60"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          className="flex h-[4.4rem] items-center justify-center gap-[0.6rem] rounded-full bg-brand-secondary px-[2rem] text-[1.6rem] font-semibold leading-[1.5] text-white transition-colors hover:bg-brand-secondary-darker disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending
            ? 'Posting...'
            : replyingTo
            ? 'Post Reply'
            : 'Post Comment'}
        </button>
      </div>
    </div>
  );
}

interface CommentNode {
  comment: CommentDocument;
  children: CommentNode[];
}

function buildCommentTree(comments: CommentDocument[]): CommentNode[] {
  const byId = new Map<string, CommentNode>();
  comments.forEach((c) => byId.set(c.id, { comment: c, children: [] }));

  const roots: CommentNode[] = [];
  comments.forEach((c) => {
    const node = byId.get(c.id);
    if (!node) return;
    if (c.replying_to && byId.has(c.replying_to)) {
      byId.get(c.replying_to)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

interface CommentTreeProps {
  nodes: CommentNode[];
  depth: number;
  isLoggedIn: boolean;
  productId: string;
  replyingToId: string | null;
  setReplyingToId: (id: string | null) => void;
  currentUserPhoto?: string;
  currentUsername?: string;
  currentUserId?: string;
  parentUsername?: string;
  onDelete?: (commentId: string) => void;
  deletingId?: string | null;
}

function CommentTree({
  nodes,
  depth,
  isLoggedIn,
  productId,
  replyingToId,
  setReplyingToId,
  currentUserPhoto,
  currentUsername,
  currentUserId,
  parentUsername,
  onDelete,
  deletingId,
}: CommentTreeProps) {
  if (nodes.length === 0) return null;

  // Indent every reply level once. Nested replies render structurally deeper
  // (so `parentUsername` resolves to the actual comment replied to) but stay at
  // a single visual indent.
  const isReply = depth > 0;
  const shouldIndent = depth >= 1;

  return (
    <>
      {nodes.map(({ comment, children }, index) => (
        <div key={comment.id} className="flex w-full flex-col">
          <div
            className={cn(
              'flex w-full flex-col items-start bg-white py-[1.6rem]',
              !(depth === 0 && index === 0) &&
                'border-t border-solid border-[#D9D9D9]'
            )}
            style={shouldIndent ? { marginLeft: `${INDENT_PX}px` } : undefined}
          >
            <div className="flex w-full items-start gap-[1.6rem]">
              <CommentAvatar
                src={comment.profile_picture}
                username={comment.username}
              />
              <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-4">
                <div className="flex w-full flex-col items-start gap-4">
                  <div className="flex items-center gap-[0.8rem] whitespace-nowrap">
                    <p className="text-[1.6rem] font-semibold leading-[1.5] text-black">
                      {comment.username || 'Anonymous'}
                    </p>
                    {isReply && parentUsername && (
                      <div className="flex items-center gap-[0.4rem]">
                        <ArrowUturnLeftIcon className="size-8 text-brand-secondary" />
                        <span className="text-[1.6rem] font-semibold leading-[1.4] text-brand-secondary">
                          replying to {parentUsername}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-[1.4rem] font-medium leading-[1.4] text-[#7F7F7F]">
                    {formatCommentDate(comment.created)}
                  </p>
                  <p className="w-full text-[1.8rem] font-medium leading-[1.5] text-[#444]">
                    {comment.comment}
                  </p>
                </div>
                {isLoggedIn && (
                  <div className="flex items-start gap-[1.6rem] whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => {
                        const newId =
                          replyingToId === comment.id ? null : comment.id;
                        setReplyingToId(newId);
                      }}
                      className="text-[1.6rem] font-semibold leading-[1.5] text-brand-secondary hover:underline"
                    >
                      {replyingToId === comment.id ? 'Cancel' : 'Reply'}
                    </button>
                    {currentUserId && comment.uid === currentUserId && (
                      <button
                        type="button"
                        disabled={deletingId === comment.id}
                        onClick={() => onDelete?.(comment.id)}
                        className="text-[1.6rem] font-semibold leading-[1.5] text-brand-secondary hover:underline disabled:opacity-50"
                      >
                        {deletingId === comment.id ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {children.length > 0 && (
            <CommentTree
              nodes={children}
              depth={depth + 1}
              isLoggedIn={isLoggedIn}
              productId={productId}
              replyingToId={replyingToId}
              setReplyingToId={setReplyingToId}
              currentUserPhoto={currentUserPhoto}
              currentUsername={currentUsername}
              currentUserId={currentUserId}
              parentUsername={comment.username}
              onDelete={onDelete}
              deletingId={deletingId}
            />
          )}

          {replyingToId === comment.id && isLoggedIn && (
            <div
              style={{
                marginLeft: `${INDENT_PX}px`,
              }}
            >
              <InlineCommentForm
                productId={productId}
                replyingTo={comment.id}
                currentUserPhoto={currentUserPhoto}
                currentUsername={currentUsername}
                autoFocus
                placeholder={`Reply to ${comment.username || 'comment'}`}
                onCancel={() => setReplyingToId(null)}
                onPosted={() => setReplyingToId(null)}
              />
            </div>
          )}
        </div>
      ))}
    </>
  );
}

function LoggedOutCTA() {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-[1.6rem] bg-white py-[4rem]">
      <div className="relative size-[6.4rem]">
        <Image
          src="/assets/comments-user.png"
          alt=""
          width={80}
          height={80}
          className="object-fill"
        />
      </div>
      <p className="text-[1.6rem] font-medium leading-[1.5] text-[#444]">
        You must be logged in to comment.
      </p>
      <Link
        href="/login"
        className="flex h-[4.4rem] items-center justify-center gap-[0.6rem] rounded-full bg-brand-secondary px-[2rem] text-[1.8rem] font-semibold leading-[1.5] text-white transition-colors hover:bg-brand-secondary-darker"
      >
        Login now
      </Link>
    </div>
  );
}

interface VehicleCommentsSectionProps {
  productId: string;
  className?: string;
}

export default function VehicleCommentsSection({
  productId,
  className,
}: VehicleCommentsSectionProps) {
  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const isLoggedIn = !!userDoc?.uid;
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) =>
      deleteProductComment(productId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['productComments', productId],
      });
      setDeletingId(null);
    },
    onError: (error) => {
      console.error('[Comments] Delete failed:', error);
      setDeletingId(null);
    },
  });

  const handleDelete = (commentId: string) => {
    if (!confirm('Delete this comment?')) return;
    setDeletingId(commentId);
    deleteMutation.mutate(commentId);
  };

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['productComments', productId],
    queryFn: () => getProductComments(productId),
    enabled: !!productId,
  });

  const tree = useMemo(() => buildCommentTree(comments), [comments]);
  const commentCount = comments.length;
  const headerLabel =
    commentCount === 1 ? '1 comment' : `${commentCount} comments`;

  return (
    <section
      className={cn(
        'flex w-full flex-col items-center gap-[1.6rem] bg-white px-12 py-8 sm:gap-[2.4rem] sm:px-0 sm:py-[6.4rem]',
        className
      )}
    >
      <div className="flex w-full items-center justify-between">
        <p className="text-[2.1rem] font-bold leading-[1.3] tracking-[-0.018em] text-black sm:text-[2.4rem] sm:tracking-[-0.01em]">
          {headerLabel}
        </p>
      </div>

      <div className="flex w-full flex-col items-start">
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-[2.4rem]">
            <p className="text-[1.4rem] font-medium text-[#7F7F7F]">
              Loading comments...
            </p>
          </div>
        ) : commentCount === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-[1.6rem] py-[8rem]">
            <Image
              src="/icons/seller/accurate_describer_accolade.png"
              alt=""
              width={128}
              height={128}
              className="size-[9.6rem] object-contain grayscale"
            />
            <p className="text-[1.8rem] font-medium text-[#7F7F7F]">
              No comments yet. Be the first to leave one.
            </p>
          </div>
        ) : (
          <div className="flex w-full flex-col items-end justify-center">
            <CommentTree
              nodes={tree}
              depth={0}
              isLoggedIn={isLoggedIn}
              productId={productId}
              replyingToId={replyingToId}
              setReplyingToId={setReplyingToId}
              currentUserPhoto={userDoc?.photo}
              currentUsername={userDoc?.username ?? ''}
              currentUserId={userDoc?.uid}
              onDelete={handleDelete}
              deletingId={deletingId}
            />
          </div>
        )}

        {isLoggedIn ? (
          <InlineCommentForm
            productId={productId}
            currentUserPhoto={userDoc?.photo}
            currentUsername={userDoc?.username ?? ''}
          />
        ) : (
          <LoggedOutCTA />
        )}
      </div>
    </section>
  );
}
